/**
 * PowerReportDetailPage Component
 * Displays full details of a single power report
 * Mobile-friendly Tailwind version
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  PaperAirplaneIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  InformationCircleIcon,
  ArrowsRightLeftIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { useOathAuth } from '../../../services/oathAuthContext';
import {
  getReportDetails,
  deleteReport,
  deletePowerFromReport,
  getExecutingAgents,
  getCities,
  getAvailablePowers,
  updateReportNumber,
  getExecutingTransferBonds,
  checkRateChanges,
  validatePowerStatuses
} from '../../../services/oathPowerReportService';
import {
  OathPageWrapper,
  OathContentCard,
  OathButton,
  OathSpinner,
  OathAlert,
  OathModal,
  OathConfirmDialog
} from '../../shared/OathUI';
import PowersTable from './components/PowersTable';
import { withErrorBoundary } from './components/shared';
import AddEditPowerModal from './modals/AddEditPowerModal';
import SubmitReportModal from './modals/SubmitReportModal';
import BatchSelectionModal from './modals/BatchSelectionModal';
import ProofUploadModal from './modals/ProofUploadModal';
import RewritePowerModal from './modals/RewritePowerModal';
import UnexecutedTransferModal from './modals/UnexecutedTransferModal';
import InvalidPowersModal from './modals/InvalidPowersModal';
import RateChangeDialog from './modals/RateChangeDialog';
import { formatCurrency, formatDate } from './utils';

const PowerReportDetailPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { user } = useOathAuth();

  // Local state for agents/cities (replaces context)
  const [executingAgents, setExecutingAgents] = useState([]);
  const [cities, setCities] = useState([]);
  const [agentDataLoaded, setAgentDataLoaded] = useState(false);
  const [preloadedPowers, setPreloadedPowers] = useState([]);
  const [cachedAgencyId, setCachedAgencyId] = useState(null);
  const [isLoadingAgentData, setIsLoadingAgentData] = useState(false);

  // State
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePowerDialogOpen, setDeletePowerDialogOpen] = useState(false);
  const [powerToDelete, setPowerToDelete] = useState(null);
  const [addEditModalOpen, setAddEditModalOpen] = useState(false);
  const [powerToEdit, setPowerToEdit] = useState(null);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [batchSelectionModalOpen, setBatchSelectionModalOpen] = useState(false);
  const [selectedBatches, setSelectedBatches] = useState(null);
  const [batchPaymentAmounts, setBatchPaymentAmounts] = useState(null);
  const [selectedBatchesData, setSelectedBatchesData] = useState(null);
  const [proofDialogOpen, setProofDialogOpen] = useState(false);
  const [powerForProof, setPowerForProof] = useState(null);
  const [rewriteModalOpen, setRewriteModalOpen] = useState(false);

  // Transfer bonds modal state
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferPowers, setTransferPowers] = useState([]);

  // Rate change detection state
  const [rateChangeDialogOpen, setRateChangeDialogOpen] = useState(false);
  const [rateChangeData, setRateChangeData] = useState(null);
  const [isCheckingTransfers, setIsCheckingTransfers] = useState(false);
  const [isAddingTransfers, setIsAddingTransfers] = useState(false);

  // Pre-submission power status validation state
  const [invalidPowersModalOpen, setInvalidPowersModalOpen] = useState(false);
  const [validationData, setValidationData] = useState(null);
  const [isValidatingStatuses, setIsValidatingStatuses] = useState(false);
  const [isRemovingInvalid, setIsRemovingInvalid] = useState(false);

  // Report number editing state
  const [isEditingReportNumber, setIsEditingReportNumber] = useState(false);
  const [reportNumberSuffix, setReportNumberSuffix] = useState('');
  const [savingReportNumber, setSavingReportNumber] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  // Ref-based re-entrancy guard: blur and the ✓ click can both fire for one save
  const savingReportNumberRef = useRef(false);
  const reportNumberInputRef = useRef(null);

  // Load report details
  // When silent=true, skips the full-page loading spinner so open modals stay mounted
  const loadReportDetails = useCallback(async ({ silent = false, skipRateCheck = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const result = await getReportDetails(reportId);
      if (result.success) {
        setReport(result.data);

        // Check for rate changes on draft (active) reports
        if (!skipRateCheck && result.data?.status?.toUpperCase() !== 'SUBMITTED' && result.data?.powers?.length > 0) {
          checkForRateChanges(reportId);
        }
      } else {
        toast.error('Failed to load report details: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Failed to load report details: ' + error.message);
      console.error('Error loading report details:', error);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [reportId]);

  // Check if commission rates have changed for this report's powers
  const checkForRateChanges = async (id) => {
    try {
      const result = await checkRateChanges(id);
      if (result.success && result.data?.hasChanges) {
        setRateChangeData(result.data);
        setRateChangeDialogOpen(true);
      }
    } catch (error) {
      console.error('Error checking rate changes:', error);
    }
  };

  // Handle rate change recalculation completing
  const handleRateChangeRecalculated = (updatedReport) => {
    setReport(updatedReport);
    setRateChangeDialogOpen(false);
    setRateChangeData(null);
  };

  // Get agencyId from the report (not from user's first agency)
  const reportAgencyId = report?.agencyId;

  // Preload agents and cities using the report's agency
  // Use ref to track loading state to avoid race conditions with useCallback dependencies
  const isLoadingRef = useRef(false);

  const loadAgentsAndCities = useCallback(async (agencyId) => {
    if (!agencyId || isLoadingRef.current) return;

    try {
      isLoadingRef.current = true;
      setIsLoadingAgentData(true);

      // GA users should see all powers across their hierarchy, not just the SA's powers
      const isGa = user?.agencies?.some(a => a.isGeneralAgent);
      const gaMainId = user?.agencies?.find(a => a.isMainAgency)?.agencyId;
      const powersAgencyId = isGa && gaMainId ? gaMainId : agencyId;

      const [agentsResult, citiesResult, powersResult] = await Promise.all([
        getExecutingAgents(agencyId),
        getCities(agencyId),
        getAvailablePowers(powersAgencyId)
      ]);

      const agents = agentsResult.success ? agentsResult.data : [];
      const citiesData = citiesResult.success ? citiesResult.data : [];
      const powersData = powersResult.success ? (Array.isArray(powersResult.data) ? powersResult.data : []) : [];

      setExecutingAgents(agents);
      setCities(citiesData);
      setPreloadedPowers(powersData);
      setCachedAgencyId(agencyId);
      setAgentDataLoaded(true);
    } catch (error) {
      console.error('Error preloading agents and cities:', error);
    } finally {
      isLoadingRef.current = false;
      setIsLoadingAgentData(false);
    }
  }, [user]);

  useEffect(() => {
    loadReportDetails();
  }, [loadReportDetails]);

  // Load agents and cities when report agency is available
  useEffect(() => {
    if (reportAgencyId && (!agentDataLoaded || cachedAgencyId !== reportAgencyId)) {
      loadAgentsAndCities(reportAgencyId);
    }
  }, [reportAgencyId, agentDataLoaded, cachedAgencyId, loadAgentsAndCities]);

  // Report number helpers
  const getReportNumberSuffix = (reportNumber) => {
    if (!reportNumber) return '';
    const parts = reportNumber.split(':');
    return parts.length > 1 ? parts[1] : reportNumber;
  };

  const getReportNumberPrefix = (reportNumber) => {
    if (!reportNumber) return '';
    const parts = reportNumber.split(':');
    return parts[0];
  };

  // Unsaved edits exist when the typed suffix differs from what's on the report
  const isReportNumberDirty =
    isEditingReportNumber &&
    reportNumberSuffix.trim() !== getReportNumberSuffix(report?.reportNumber).trim();

  const handleStartEditReportNumber = () => {
    setReportNumberSuffix(getReportNumberSuffix(report?.reportNumber));
    setIsEditingReportNumber(true);
  };

  const closeReportNumberEditor = () => {
    setIsEditingReportNumber(false);
    setReportNumberSuffix('');
    setDiscardConfirmOpen(false);
  };

  // Explicit cancel (✗ button or Escape) — confirm before throwing away edits
  const handleCancelEditReportNumber = () => {
    if (isReportNumberDirty && reportNumberSuffix.trim()) {
      setDiscardConfirmOpen(true);
      return;
    }
    closeReportNumberEditor();
  };

  const handleSaveReportNumber = async () => {
    if (savingReportNumberRef.current) return;

    if (!isReportNumberDirty) {
      closeReportNumberEditor();
      return;
    }

    const trimmedSuffix = reportNumberSuffix.trim();
    if (!trimmedSuffix) {
      toast.error('Report name cannot be empty');
      reportNumberInputRef.current?.focus();
      return;
    }
    if (!/^[a-zA-Z0-9 .\-_/()]+$/.test(trimmedSuffix)) {
      toast.error('Report number can only contain letters, numbers, spaces, and common punctuation (. - _ / ( ))');
      reportNumberInputRef.current?.focus();
      return;
    }

    try {
      savingReportNumberRef.current = true;
      setSavingReportNumber(true);
      const result = await updateReportNumber(reportId, trimmedSuffix);
      if (result.success) {
        setReport(prev => ({ ...prev, reportNumber: result.data }));
        closeReportNumberEditor();
        toast.success('Report number updated successfully');
      } else {
        toast.error('Failed to update report number: ' + (result.error || 'Unknown error'));
        reportNumberInputRef.current?.focus();
      }
    } catch (error) {
      toast.error('Failed to update report number: ' + error.message);
      reportNumberInputRef.current?.focus();
    } finally {
      savingReportNumberRef.current = false;
      setSavingReportNumber(false);
    }
  };

  // Auto-save when focus leaves the editor (legacy portal saved without an explicit
  // save click, so agents click elsewhere assuming their change stuck)
  const handleReportNumberBlur = (e) => {
    // Focus moved within the editor (input ⇄ save/cancel buttons) — not a leave
    if (e.currentTarget.contains(e.relatedTarget)) return;
    // A save is already in flight, or the discard prompt owns the interaction
    if (savingReportNumberRef.current || discardConfirmOpen) return;

    if (!isReportNumberDirty) {
      closeReportNumberEditor();
      return;
    }

    if (!reportNumberSuffix.trim()) {
      closeReportNumberEditor();
      toast.info('Report number not changed — the name cannot be empty');
      return;
    }

    handleSaveReportNumber();
  };

  // Warn before refresh/tab-close while there are unsaved report number edits
  useEffect(() => {
    if (!isReportNumberDirty) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isReportNumberDirty]);

  // Action handlers
  const handleDeleteReport = async () => {
    try {
      const result = await deleteReport(reportId);
      if (result.success) {
        toast.success('Report deleted successfully');
        navigate('/app/power-reports');
      } else {
        toast.error('Failed to delete report: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Failed to delete report: ' + error.message);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleEditPower = (power) => {
    setPowerToEdit(power);
    setAddEditModalOpen(true);
  };

  const handleDeletePower = (powerId) => {
    setPowerToDelete(powerId);
    setDeletePowerDialogOpen(true);
  };

  const confirmDeletePower = async () => {
    try {
      const result = await deletePowerFromReport(reportId, powerToDelete);
      if (result.success) {
        toast.success('Power deleted successfully');
        setPreloadedPowers([]); // Clear so modal fetches fresh powers
        await loadReportDetails();
      } else {
        toast.error('Failed to delete power: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Failed to delete power: ' + error.message);
    } finally {
      setDeletePowerDialogOpen(false);
      setPowerToDelete(null);
    }
  };

  const handleAddPower = () => {
    setPowerToEdit(null);
    setAddEditModalOpen(true);
  };

  const handlePowerSaved = async () => {
    // Clear preloaded powers so the modal fetches fresh data after add/edit
    setPreloadedPowers([]);
    // Use silent refresh so the modal stays mounted (preserves form state for Save+Copy / Save+New)
    await loadReportDetails({ silent: true });
  };

  // Validate power statuses and proceed to batch selection if all valid
  const validateAndProceed = async () => {
    setIsValidatingStatuses(true);
    try {
      const result = await validatePowerStatuses(reportId);
      if (result.success && result.data) {
        if (result.data.isValid) {
          setBatchSelectionModalOpen(true);
        } else {
          setValidationData(result.data);
          setInvalidPowersModalOpen(true);
        }
      } else {
        // Validation API failed — proceed anyway (defense-in-depth on submit catches it)
        toast.warning('Could not validate power statuses. Proceeding with submission.');
        setBatchSelectionModalOpen(true);
      }
    } catch (error) {
      console.error('Error validating power statuses:', error);
      toast.warning('Could not validate power statuses. Proceeding with submission.');
      setBatchSelectionModalOpen(true);
    } finally {
      setIsValidatingStatuses(false);
    }
  };

  // Check for unreported transfer bonds before proceeding with submission
  const handleSubmitReport = async () => {
    // Fresh Submit attempt — discard any batch selection left over from a previously
    // cancelled flow so BatchSelectionModal opens clean (Back from SubmitReportModal
    // is the only path that preserves selection).
    setSelectedBatches(null);
    setBatchPaymentAmounts(null);
    setSelectedBatchesData(null);

    if (!report?.agencyId) {
      await validateAndProceed();
      return;
    }

    setIsCheckingTransfers(true);
    try {
      const result = await getExecutingTransferBonds(report.agencyId);
      if (result.success && result.data && result.data.length > 0) {
        // There are unreported transfer powers - show the modal
        setTransferPowers(result.data);
        setTransferModalOpen(true);
      } else {
        // No transfer powers - proceed to status validation
        await validateAndProceed();
      }
    } catch (error) {
      console.error('Error checking transfer bonds:', error);
      // On error, proceed with validation anyway
      await validateAndProceed();
    } finally {
      setIsCheckingTransfers(false);
    }
  };

  // Handle adding selected transfer powers to the report
  // Calls backend endpoint that uses existing transfer bond data
  const handleAddTransferPowers = async (selectedPowers) => {
    if (selectedPowers.length === 0) {
      // No powers selected, just continue to validation
      handleTransfersContinue();
      return;
    }

    setIsAddingTransfers(true);
    try {
      // Get power IDs from selected powers
      const powerIds = selectedPowers.map(p => p.powerId || p.PowerId);

      // Call backend to add transfer powers using existing transfer bond data
      const { addTransferPowersToReport } = await import('../../../services/oathPowerReportService');
      const result = await addTransferPowersToReport(reportId, powerIds);

      if (result.success) {
        toast.success(`Added ${selectedPowers.length} transfer power${selectedPowers.length !== 1 ? 's' : ''} to report`);
        await loadReportDetails();
        handleTransfersContinue();
      } else {
        toast.error('Failed to add transfer powers: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error adding transfer powers:', error);
      toast.error('Failed to add transfer powers: ' + error.message);
    } finally {
      setIsAddingTransfers(false);
    }
  };

  // Continue to status validation after handling transfers
  const handleTransfersContinue = () => {
    setTransferModalOpen(false);
    setTransferPowers([]);
    validateAndProceed();
  };

  // Remove invalid powers and auto-proceed to batch selection
  const handleRemoveAndContinue = async () => {
    if (!validationData?.invalidPowers?.length) return;

    setIsRemovingInvalid(true);
    try {
      const results = await Promise.allSettled(
        validationData.invalidPowers.map(p =>
          deletePowerFromReport(reportId, p.powerReportPowerId)
        )
      );

      const succeeded = results.filter(r => r.status === 'fulfilled' && r.value?.success).length;
      const failed = results.length - succeeded;

      setInvalidPowersModalOpen(false);
      setValidationData(null);

      // Refresh report data
      await loadReportDetails({ silent: true });

      if (failed === 0) {
        toast.success(`Removed ${succeeded} invalid power${succeeded !== 1 ? 's' : ''}`);
        // Auto-proceed to batch selection
        setBatchSelectionModalOpen(true);
      } else {
        toast.warning(`Removed ${succeeded} power${succeeded !== 1 ? 's' : ''}, but ${failed} failed. Please try again.`);
      }
    } catch (error) {
      console.error('Error removing invalid powers:', error);
      toast.error('Failed to remove invalid powers: ' + error.message);
    } finally {
      setIsRemovingInvalid(false);
    }
  };

  const handleBatchSelectionContinue = (batches, amounts, batchData) => {
    setSelectedBatches(batches);
    setBatchPaymentAmounts(amounts);
    setSelectedBatchesData(batchData);
    setBatchSelectionModalOpen(false);
    setSubmitModalOpen(true);
  };

  const handleBatchSelectionSkip = () => {
    setSelectedBatches(null);
    setBatchPaymentAmounts(null);
    setSelectedBatchesData(null);
    setBatchSelectionModalOpen(false);
    setSubmitModalOpen(true);
  };

  // Back from SubmitReportModal → reopen BatchSelectionModal with the user's prior picks preserved.
  const handleBackToBatchSelection = () => {
    setSubmitModalOpen(false);
    setBatchSelectionModalOpen(true);
  };

  const handleUploadProof = (power) => {
    setPowerForProof(power);
    setProofDialogOpen(true);
  };

  const handleProofUpdated = async () => {
    // Use silent refresh so the page doesn't flash a loading spinner
    await loadReportDetails({ silent: true });
  };

  const handleRewriteSuccess = async () => {
    await loadReportDetails();
  };

  const handleSubmitSuccess = (response) => {
    toast.success(`Report ${response.reportNumber} submitted successfully!`);
    navigate('/app/power-reports', {
      state: {
        showPdf: true,
        pdfData: response.pdfBase64,
        reportNumber: response.reportNumber,
        activeTab: 1
      }
    });
  };

  // Loading state
  if (loading) {
    return (
      <OathPageWrapper>
        <div className="flex flex-col items-center justify-center py-16">
          <OathSpinner size="lg" />
          <p className="mt-4 text-gray-500 dark:text-gray-400">Loading report details...</p>
        </div>
      </OathPageWrapper>
    );
  }

  // Error state
  if (!report) {
    return (
      <OathPageWrapper>
        <OathAlert variant="error" className="mb-4">
          Report not found or you do not have permission to view it.
        </OathAlert>
        <OathButton
          variant="outline"
          icon={ArrowLeftIcon}
          onClick={() => navigate('/app/power-reports')}
        >
          Back to Power Reporting
        </OathButton>
      </OathPageWrapper>
    );
  }

  // Filter out transfer powers - they should only appear in the PDF, not the UI
  const powers = (report.powers || []).filter(p => !p.isTransfer);
  // Use actual powers array length as primary source (more accurate than stored powerCount)
  const powerCount = powers.length || report.powerCount || 0;
  const totalNet = report.totalNet || 0;
  const totalBuf = report.totalBuf || 0;
  const totalPremium = report.totalPremium || 0;
  const totalPowerAmount = report.totalBondAmount || 0;
  const hasLegacyPowers = powers.some(p => p.powerReportPowerId === 0);
  const isSubmitted = report.status?.toUpperCase() === 'SUBMITTED';

  return (
    <OathPageWrapper maxWidth="max-w-7xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <OathButton
          variant="outline"
          icon={ArrowLeftIcon}
          onClick={() => navigate('/app/power-reports')}
          size="sm"
        >
          Back
        </OathButton>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          Power Report Details
        </h1>
      </div>

      {/* Legacy Powers Warning */}
      {hasLegacyPowers && (
        <OathAlert variant="info" className="mb-4">
          <div className="flex items-start gap-2">
            <InformationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm">
              This report contains powers created in the legacy system. Legacy powers can only be modified through the internal BSS application.
            </p>
          </div>
        </OathAlert>
      )}

      {/* Report Header Card */}
      <OathContentCard className="mb-6">
        {/* Title Bar */}
        <div className="bg-primary dark:bg-primary-dark text-white px-4 sm:px-6 py-3 -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 mb-4 rounded-t-2xl">
          <h2 className="text-lg sm:text-xl font-bold">
            Execution Report for: {reportAgencyId || 'N/A'} {report.agencyName || user?.agencies?.[0]?.dbaAgencyName || 'N/A'}
          </h2>
        </div>

        {/* Report Info Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            {/* Report Number */}
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Report Number
              </p>
              {isEditingReportNumber ? (
                <div onBlur={handleReportNumberBlur}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold font-mono text-gray-900 dark:text-white">
                      {getReportNumberPrefix(report.reportNumber)}:
                    </span>
                    <input
                      ref={reportNumberInputRef}
                      type="text"
                      value={reportNumberSuffix}
                      onChange={(e) => setReportNumberSuffix(e.target.value)}
                      disabled={savingReportNumber}
                      placeholder="Enter name"
                      className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono font-bold focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveReportNumber();
                        if (e.key === 'Escape') handleCancelEditReportNumber();
                      }}
                    />
                    <button
                      onClick={handleSaveReportNumber}
                      onMouseDown={(e) => e.preventDefault()}
                      disabled={savingReportNumber || !reportNumberSuffix.trim()}
                      title="Save report number"
                      className="p-1.5 text-primary hover:bg-primary/10 rounded-full disabled:opacity-50"
                    >
                      {savingReportNumber ? <OathSpinner size="sm" /> : <CheckIcon className="h-5 w-5" />}
                    </button>
                    <button
                      onClick={handleCancelEditReportNumber}
                      onMouseDown={(e) => e.preventDefault()}
                      disabled={savingReportNumber}
                      title="Cancel editing"
                      className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                    Saved automatically when you click away or press Enter
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold font-mono text-gray-900 dark:text-white">
                    {report.reportNumber || 'N/A'}
                  </span>
                  {!report.reportDate && (
                    <button
                      onClick={handleStartEditReportNumber}
                      className="p-1.5 text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                      title="Edit report number"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Date Created */}
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Date Created
              </p>
              <p className="text-base font-medium text-gray-900 dark:text-white">
                {formatDate(report.dateCreated)}
              </p>
            </div>
          </div>

          {/* Right Column - Totals */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Power Count</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{powerCount}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Liability</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">${formatCurrency(totalPowerAmount)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Premium</p>
                <p className="text-xl font-bold text-green-600 dark:text-green-400">${formatCurrency(totalPremium)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total NET</p>
                <p className="text-xl font-bold text-primary dark:text-primary-light">${formatCurrency(totalNet)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total BUF</p>
                <p className="text-xl font-bold text-primary dark:text-primary-light">${formatCurrency(totalBuf)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <OathButton onClick={handleAddPower} icon={PlusIcon}>
            Add Power
          </OathButton>

          <OathButton
            variant="warning"
            onClick={() => setRewriteModalOpen(true)}
            icon={ArrowsRightLeftIcon}
            title="Rewrite an executed power to a new power"
          >
            Rewrite
          </OathButton>

          {!isSubmitted && (
            <OathButton
              variant="success"
              onClick={handleSubmitReport}
              disabled={powerCount === 0 || isCheckingTransfers || isValidatingStatuses}
              icon={isCheckingTransfers || isValidatingStatuses ? null : PaperAirplaneIcon}
            >
              {isCheckingTransfers || isValidatingStatuses ? (
                <span className="flex items-center gap-2">
                  <OathSpinner size="sm" />
                  {isValidatingStatuses ? 'Validating...' : 'Checking...'}
                </span>
              ) : (
                'Submit Report'
              )}
            </OathButton>
          )}

          {!isSubmitted && (
            <OathButton
              variant="danger"
              onClick={() => setDeleteDialogOpen(true)}
              icon={TrashIcon}
            >
              Delete Report
            </OathButton>
          )}
        </div>
      </OathContentCard>

      {/* Powers List */}
      <OathContentCard>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Powers in Report
        </h3>
        <PowersTable
          powers={powers}
          onEditPower={handleEditPower}
          onDeletePower={handleDeletePower}
          onUploadProof={handleUploadProof}
          isSubmitted={isSubmitted}
          reportStatus={report.status}
        />
      </OathContentCard>

      {/* Delete Report Dialog */}
      <OathModal
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete Report?"
        size="sm"
        footer={
          <div className="flex gap-3 ml-auto">
            <OathButton variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </OathButton>
            <OathButton variant="danger" onClick={handleDeleteReport}>
              Delete
            </OathButton>
          </div>
        }
      >
        <p className="text-gray-600 dark:text-gray-400">
          Are you sure you want to delete this report? This action cannot be undone.
          All powers associated with this report will be returned to the available powers list.
        </p>
      </OathModal>

      {/* Delete Power Dialog */}
      <OathModal
        isOpen={deletePowerDialogOpen}
        onClose={() => setDeletePowerDialogOpen(false)}
        title="Delete Power?"
        size="sm"
        footer={
          <div className="flex gap-3 ml-auto">
            <OathButton variant="outline" onClick={() => setDeletePowerDialogOpen(false)}>
              Cancel
            </OathButton>
            <OathButton variant="danger" onClick={confirmDeletePower}>
              Delete
            </OathButton>
          </div>
        }
      >
        <p className="text-gray-600 dark:text-gray-400">
          Are you sure you want to delete this power from the report?
          The power will be returned to the available powers list.
        </p>
      </OathModal>

      {/* Add/Edit Power Modal */}
      <AddEditPowerModal
        isOpen={addEditModalOpen}
        onClose={() => {
          setAddEditModalOpen(false);
          setPowerToEdit(null);
        }}
        reportId={reportId}
        power={powerToEdit}
        onSave={handlePowerSaved}
        onOpenProofDialog={handleUploadProof}
        preloadedAgents={executingAgents}
        preloadedCities={cities}
        preloadedPowers={preloadedPowers}
        dataPreloaded={agentDataLoaded}
        isSouthCarolina={report?.sc || false}
        agencyId={report?.agencyId}
        existingPowers={report?.powers || []}
      />

      {/* Batch Selection Modal */}
      <BatchSelectionModal
        isOpen={batchSelectionModalOpen}
        onClose={() => setBatchSelectionModalOpen(false)}
        onContinue={handleBatchSelectionContinue}
        onSkip={handleBatchSelectionSkip}
        agencyId={report?.agencyId}
        report={report}
        initialSelectedBatches={selectedBatches}
        initialPaymentAmounts={batchPaymentAmounts}
      />

      {/* Submit Report Modal */}
      <SubmitReportModal
        isOpen={submitModalOpen}
        onClose={() => setSubmitModalOpen(false)}
        onBack={handleBackToBatchSelection}
        report={report}
        onSuccess={handleSubmitSuccess}
        selectedBatches={selectedBatches}
        batchPaymentAmounts={batchPaymentAmounts}
        batchesData={selectedBatchesData}
      />

      {/* Proof Upload Modal */}
      <ProofUploadModal
        isOpen={proofDialogOpen}
        onClose={() => {
          setProofDialogOpen(false);
          setPowerForProof(null);
        }}
        power={powerForProof}
        onProofUpdated={handleProofUpdated}
      />

      {/* Rewrite Power Modal */}
      <RewritePowerModal
        isOpen={rewriteModalOpen}
        onClose={() => setRewriteModalOpen(false)}
        onSuccess={handleRewriteSuccess}
        reportId={reportId}
        reportStatus={report?.status}
        powers={powers}
        agencyId={report?.agencyId}
        preloadedAgents={executingAgents}
        preloadedCities={cities}
      />

      {/* Unexecuted Transfer Bonds Modal */}
      <UnexecutedTransferModal
        isOpen={transferModalOpen}
        onClose={() => {
          setTransferModalOpen(false);
          setTransferPowers([]);
        }}
        transferPowers={transferPowers}
        onContinue={handleTransfersContinue}
        onAddTransfers={handleAddTransferPowers}
        isAdding={isAddingTransfers}
      />

      {/* Invalid Powers Modal (pre-submission status check) */}
      <InvalidPowersModal
        isOpen={invalidPowersModalOpen}
        onClose={() => {
          setInvalidPowersModalOpen(false);
          setValidationData(null);
        }}
        validationData={validationData}
        reportId={reportId}
        onRemoveInvalid={handleRemoveAndContinue}
        isRemoving={isRemovingInvalid}
      />

      {/* Discard unsaved report number changes confirmation */}
      <OathConfirmDialog
        open={discardConfirmOpen}
        title="Discard unsaved changes?"
        message={`The report name "${reportNumberSuffix.trim()}" has not been saved yet.`}
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="warning"
        onConfirm={closeReportNumberEditor}
        onCancel={() => {
          setDiscardConfirmOpen(false);
          reportNumberInputRef.current?.focus();
        }}
      />

      {/* Rate Change Blocking Dialog */}
      <RateChangeDialog
        isOpen={rateChangeDialogOpen}
        rateChangeData={rateChangeData}
        reportId={reportId}
        onRecalculated={handleRateChangeRecalculated}
      />
    </OathPageWrapper>
  );
};

export default withErrorBoundary(PowerReportDetailPage, {
  title: 'Report Detail Error',
  message: 'An error occurred while loading the report details. Please try again.'
});
